import React from "react";
import { DEFAULT_COLORS, colorVariables } from "./colors";
import { alpha, createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";

const foreground = (theme) =>
  theme.palette.mode === "dark"
    ? theme.palette.common.white
    : theme.palette.common.black;
const background = (theme) =>
  theme.palette.mode === "dark"
    ? theme.palette.common.black
    : theme.palette.common.white;
const highlightedOption = (theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: background(theme),
});
const dropdownScrollArea = (theme) => ({
  scrollbarGutter: "stable",
  paddingInlineEnd: 8,
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.grey[500]} ${background(theme)}`,
  overflowX: "hidden",
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { backgroundColor: background(theme) },
  "&::-webkit-scrollbar-thumb": { backgroundColor: theme.palette.grey[500] },
});
const dropdownSurface = ({ theme }) => ({
  borderRadius: 0,
  backgroundImage: "none",
  backgroundColor: background(theme),
  color: foreground(theme),
  "& .MuiListSubheader-root": {
    backgroundColor: background(theme),
    color: "inherit",
  },
  "& .MuiListItemText-secondary, & .MuiListItemIcon-root": { color: "inherit" },
  "& .MuiMenuItem-root": {
    color: "inherit",
    "&:hover, &.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible":
      highlightedOption(theme),
  },
});

export default function AppTheme({
  darkMode = false,
  colors: overrides,
  children,
}) {
  const colors = React.useMemo(
    () => ({ ...DEFAULT_COLORS, ...overrides }),
    [overrides],
  );
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          common: { black: colors.black, white: colors.white },
          primary: { main: colors.orange },
          text: {
            primary: darkMode ? colors.white : colors.black,
            secondary: alpha(darkMode ? colors.white : colors.black, 0.7),
            disabled: alpha(darkMode ? colors.white : colors.black, 0.38),
          },
          divider: alpha(darkMode ? colors.white : colors.black, 0.12),
          secondary: {
            main: darkMode ? colors.secondaryDark : colors.secondaryLight,
          },
          background: {
            default: darkMode ? colors.pageDark : colors.pageLight,
            paper: darkMode ? colors.charcoal : colors.white,
          },
        },
        typography: {
          fontFamily: "Inter, system-ui, sans-serif",
          button: { textTransform: "none" },
        },
        shape: { borderRadius: 8 },
        components: {
          MuiButtonBase: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&.Mui-focusVisible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }),
            },
          },
          MuiButton: {
            defaultProps: { variant: "outlined", size: "small" },
            styleOverrides: {
              root: ({ theme }) => ({
                ".filter-dropdown &": {
                  borderRadius: "4px",
                  backgroundColor: background(theme),
                  color: foreground(theme),
                  "&:hover": {
                    backgroundColor: background(theme),
                    color: theme.palette.primary.main,
                  },
                  "&.Mui-disabled": { color: theme.palette.text.disabled },
                },
                "&.dropdown-toggle": {
                  minHeight: "2rem",
                  padding: "3px 10px",
                  borderColor: alpha(foreground(theme), 0.23),
                  "&:hover": { borderColor: theme.palette.text.primary },
                },
                minHeight: 40,
                paddingInline: 12,
                lineHeight: 1.4,
                gap: 6,
                "@media (pointer: coarse)": { minHeight: 44 },
              }),
            },
          },
          MuiIconButton: {
            defaultProps: { size: "small", disableRipple: true },
            styleOverrides: {
              root: ({ theme }) => ({
                color: foreground(theme),
                backgroundColor: "transparent",
                "&:hover, &.Mui-selected, &[aria-pressed='true'], &[aria-expanded='true'], &.MuiAutocomplete-popupIndicatorOpen":
                  {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent",
                  },
                width: 40,
                height: 40,
                flexShrink: 0,
                "@media (pointer: coarse)": { width: 44, height: 44 },
              }),
            },
          },
          MuiTextField: {
            defaultProps: { size: "small", variant: "outlined" },
            styleOverrides: { root: { minWidth: 0, maxWidth: "100%" } },
          },
          MuiInputBase: {
            styleOverrides: { root: { minWidth: 0, fontSize: "0.875rem" } },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                "& .MuiOutlinedInput-input": { padding: "6px 10px" },
                "& .MuiInputBase-inputMultiline": { padding: 0 },
                "&.MuiInputBase-multiline": { padding: "6px 10px" },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: { fontSize: "0.875rem" },
              outlined: {
                "&.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)": {
                  transform: "translate(10px, 7px) scale(1)",
                },
              },
            },
          },
          MuiSelect: {
            defaultProps: { size: "small" },
            styleOverrides: {
              icon: ({ theme }) => ({
                color: foreground(theme),
                ".MuiInputBase-root:hover &, &.MuiSelect-iconOpen": {
                  color: theme.palette.primary.main,
                },
              }),
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: ({ theme }) => ({
                ...dropdownSurface({ theme }),
                ...dropdownScrollArea(theme),
              }),
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                minHeight: 32,
                padding: "4px 12px",
                fontSize: "0.875rem",
                "@media (min-width: 600px)": { minHeight: 32 },
                "@media (pointer: coarse)": { minHeight: 44 },
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              paper: dropdownSurface,
              listbox: ({ theme }) => dropdownScrollArea(theme),
              inputRoot: {
                "&.MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                  paddingTop: 2,
                  paddingBottom: 2,
                },
              },
              option: ({ theme }) => ({
                minHeight: 32,
                padding: "4px 12px",
                fontSize: "0.875rem",
                "@media (min-width: 600px)": { minHeight: 32 },
                "@media (pointer: coarse)": { minHeight: 44 },
                "&:hover, &.Mui-focused, &.Mui-focusVisible, &[aria-selected='true'], &[aria-selected='true'].Mui-focused":
                  highlightedOption(theme),
              }),
            },
          },
          MuiDialog: {
            defaultProps: { fullWidth: true, maxWidth: "xs" },
            styleOverrides: {
              paper: {
                "@media (max-width: 600px)": {
                  margin: 16,
                  width: "calc(100% - 32px)",
                },
              },
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: { padding: "16px 24px", gap: 8, flexWrap: "wrap" },
            },
          },
          MuiAlert: {
            styleOverrides: {
              message: { minWidth: 0, overflowWrap: "anywhere" },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              head: { fontWeight: 600 },
              root: { fontSize: "0.875rem" },
            },
          },
          MuiTooltip: { defaultProps: { enterTouchDelay: 0 } },
        },
      }),
    [darkMode, colors],
  );
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ":root": colorVariables(colors),
          body: {
            padding: "1rem",
            "--color-primary": theme.palette.primary.main,
            "--color-secondary": theme.palette.secondary.main,
            "--dropdown-bg": background(theme),
            "--panel-bg": theme.palette.background.paper,
            "--content-bg": darkMode ? colors.black : colors.white,
            "--content-fg": darkMode ? colors.white : colors.black,
            "--content-divider": darkMode
              ? colors.rowDivider
              : theme.palette.divider,
            "--dropdown-fg": foreground(theme),
            "--text-secondary": theme.palette.text.secondary,
            "--text-primary": theme.palette.text.primary,
            "--surface-muted": darkMode ? colors.charcoal : colors.mutedLight,
            "--border-color": theme.palette.divider,
            "--color-error": theme.palette.error.main,
            "--color-on-badge": "var(--color-white)",
            "--search-background": darkMode ? colors.black : colors.white,
            "--search-foreground": darkMode ? colors.white : colors.black,
          },
        }}
      />
      {children}
    </ThemeProvider>
  );
}
